import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { ShieldAlert } from "lucide-react";
import AuthShell, { Field } from "@/components/AuthShell";
import { ErrorBox, Spinner } from "@/components/Bits";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { CONSENT_TEXT, LocationFields, useGeo } from "@/components/PrivacySettings";

export default function Register() {
  const [params] = useSearchParams();
  const token = params.get("invitation") || "";
  const expSlug = token ? "" : params.get("experience") || "";
  const [invite, setInvite] = useState(token ? null : expSlug ? { valid: true, experience: true } : { valid: false, reason: "L'inscription nécessite un lien d'invitation valide." });
  const [form, setForm] = useState({ display_name: "", email: "", password: "", accept_terms: false });
  const [consents, setConsents] = useState({ marketing_email: false, location_offers: false, ad_measurement: false });
  const [loc, setLoc] = useState({});
  const geo = useGeo();
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const { refreshUser, user } = useAuth();
  const nav = useNavigate();

  useEffect(() => {
    if (token) api.get(`/invitations/${encodeURIComponent(token)}`).then(({ data }) => setInvite(data)).catch((e) => setInvite({ valid: false, reason: errMsg(e) }));
  }, [token]);

  useEffect(() => {
    if (user && token) nav(`/invitation/${token}`, { replace: true });
  }, [user, token, nav]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      await api.post("/auth/register", { ...form, invitation_token: token || null, experience_slug: expSlug || null, consents, location: loc.country ? { country: loc.country, region: loc.region || null, city: loc.city || null } : null });
      await refreshUser();
      nav(expSlug ? `/experience/${expSlug}` : "/tableau-de-bord", { replace: true });
    } catch (err) {
      setError(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  if (!invite) return <AuthShell title="Inscription"><Spinner /></AuthShell>;
  if (!invite.valid)
    return (
      <AuthShell title="Inscription" subtitle="Ma Petite Ligue est une application privée.">
        <div data-testid="register-invalid-invite" className="mpl-card flex gap-3 p-5 text-sm">
          <ShieldAlert className="shrink-0 text-amber-300" size={22} />
          <div>
            <p className="font-semibold text-white">{invite.reason}</p>
            <p className="mt-1 text-slate-400">Demandez un nouveau lien au propriétaire d'une ligue.</p>
          </div>
        </div>
        <Link to="/connexion" className="mpl-btn-ghost mt-6 w-full" data-testid="register-login-link">J'ai déjà un compte</Link>
      </AuthShell>
    );

  return (
    <AuthShell title="Créer mon compte" subtitle={invite.experience ? "Inscription à une Experience Ma Petite Ligue : tu rejoindras sa ligue juste après." : invite.kind === "organizer" ? "Invitation organisateur : vous pourrez créer votre propre ligue." : `Invitation à rejoindre « ${invite.league_name} » de ${invite.owner_name}.`}
      footer={<>Déjà un compte ? <Link to={`/connexion?suite=/invitation/${token}`} className="text-white underline" data-testid="register-to-login-link">Se connecter</Link></>}>
      <form onSubmit={submit} className="space-y-4" data-testid="register-form">
        <Field label="Nom d'affichage" hint="Visible par les membres de vos ligues.">
          <input className="mpl-input" required minLength={2} maxLength={30} value={form.display_name}
            onChange={(e) => setForm({ ...form, display_name: e.target.value })} data-testid="register-name-input" />
        </Field>
        <Field label="Adresse courriel" hint="Jamais affichée aux autres membres.">
          <input className="mpl-input" type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="register-email-input" />
        </Field>
        <Field label="Mot de passe" hint="Au moins 8 caractères.">
          <input className="mpl-input" type="password" minLength={8} required value={form.password}
            onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="register-password-input" />
        </Field>
        <label className="flex items-start gap-3 text-xs text-slate-400">
          <input type="checkbox" className="mt-0.5 h-4 w-4 accent-[#B7F52A]" checked={form.accept_terms}
            onChange={(e) => setForm({ ...form, accept_terms: e.target.checked })} data-testid="register-terms-checkbox" />
          <span>J'ai lu et j'accepte les <Link to="/conditions" className="text-white underline">Conditions d'utilisation</Link> de Ma Petite Ligue et je reconnais avoir pris connaissance de la{" "}
            <Link to="/confidentialite" className="text-white underline">Politique de confidentialité</Link>.</span>
        </label>
        <p className="text-[11px] text-slate-500" data-testid="register-partner-info">Ma Petite Ligue peut présenter des offres de partenaires, toujours identifiées comme telles. Tes coordonnées ne leur sont jamais transmises.</p>
        <details className="rounded-xl border border-white/10 p-3 text-xs text-slate-400" data-testid="register-optional">
          <summary className="cursor-pointer font-semibold text-slate-300">Choix facultatifs (modifiables à tout moment)</summary>
          <div className="mt-3 space-y-3">
            <LocationFields value={loc} onChange={setLoc} geo={geo} prefix="register-loc" />
            {Object.entries(CONSENT_TEXT).map(([k, t]) => (
              <label key={k} className="flex items-start gap-3">
                <input type="checkbox" className="mt-0.5 h-4 w-4 accent-[#B7F52A]" checked={consents[k]} onChange={(e) => setConsents({ ...consents, [k]: e.target.checked })} data-testid={`register-consent-${k}`} />
                <span>{t}</span>
              </label>
            ))}
          </div>
        </details>
        {error && <ErrorBox message={error} />}
        <button className="mpl-btn-primary w-full py-3" disabled={busy || !form.accept_terms} data-testid="register-submit-button">
          {busy ? "Création…" : "Créer mon compte et rejoindre"}
        </button>
      </form>
    </AuthShell>
  );
}
